import { Organisation, Partner } from '../admin/admin.types'

import { CurrencyCode, Money } from '@percent/utility'

export enum DonationStatus {
  PENDING = 'ACTIVE',
  REQUESTED_PAYMENT = 'REQUESTED_PAYMENT',
  RECEIVED_PAYMENT = 'RECEIVED_PAYMENT',
  DISBURSED = 'DISBURSED',
  CANCELLED = 'CANCELLED',
  SETTLED = 'SETTLED'
}

export enum DonationType {
  Hosted = 'hosted',
  Direct = 'direct',
  Matched = 'matched',
  External = 'external',
  Wallet = 'wallet',
  Payroll = 'payroll'
}

export type Donation = {
  id: string
  organisationId: string
  partnerId: string
  status: DonationStatus
  externalPaymentUrl?: string
  amount: {
    amount: number
    currency: string
  }
  createdAt: Date
  updatedAt: Date
  settledAt?: Date
  disbursedAt?: Date
  cancelledAt?: Date
  deletedAt?: Date
  userId: string
  foundationId?: string
  giftAidId: string
  anonymous?: string
  consentedToBeContactedByOrg?: string
  firstName?: string
  lastName?: string
  email?: string
  metadata?: string
  settledAmount?: string
  donationRequestId?: string
  donationSessionId?: string
  paymentIntentId?: string
  type: DonationType
  reassignedAt?: Date
  reassignedReason?: string
  reassignedFromOrganisationId?: string
  reassignedBy?: string
}

export type ReassignmentQueueBulkStatistics = {
  markedForReassignment: number
  ineligibleDonations: {
    cancelled: number
    disbursed: number
    inActiveDisbursement: number
  }
}

export type TotalDonationsCountWithTotal = {
  count: number
  converted: Money
}

export type GetDonationListProps = {
  pageSize?: number
  cursor?: string
  status?: string | null
  partnerId?: string | null
  startDate?: string | null
  endDate?: string | null
  query?: string | null
  onHoldOnly?: boolean | null
  organisationId?: string | null
  kinds?: string[] | null
}

export type GetTotalDonationsWithAmountProps = {
  organisationId: string
  currencyCode?: CurrencyCode
}

export type PendingReassignment = {
  status: 'PENDING'
  kind: ReassignmentKind
  reason?: string
  newOrganisationId?: string
}

export type DonationEnhanced = Donation & {
  partner?: Partner
  organisation?: Organisation
  pendingReassignment?: PendingReassignment
}

export type PostDonationsBatchCsvProps = {
  donationsBatchCSV: File
}

export type DonationCategory = 'grant' | 'consumer_giving' | 'employee_impact' | 'corporate_donation'

export type DonationsBatchRow = {
  majorAmount: number
  currencyCode: string
  partnerId: string
  organisationId: string
  subsidiaryId?: string
  createdAt?: string
  disbursementConfigurationType?: 'standalone' | 'bundled'
  disbursementConfigurationFundSource?: string
  disbursementConfigurationSendEmail?: boolean
  disbursementConfigurationReference?: string
  category?: DonationCategory
}

export type PostDonationsBatchProps = {
  payload: DonationsBatchRow[]
}

export type PostDonationsBatchResponse = {
  data: { donationsCount: number }
}

export type DonationReassignmentsBatchRow = {
  donationId: string
  reassignmentReason: string
  newOrganisationId: string
  oldOrganisationId: string
}

export type PostDonationReassignmentsBatchProps = {
  payload: string
}

export type PostDonationReassignmentsBatchResponse = {
  data: { reassignedCount: number }
}

export type GetDonationReassignmentListProps = {
  reassignmentKinds: ReassignmentKind[]
  reassignmentStatuses: ReassignmentStatus[]
}

export type AddDonationToReassignmentQueueProps = {
  donationId: string
  payload: {
    reason?: string
    newOrganisationId?: string
    reassignmentKind: ReassignmentKind
  }
}

export type AddAllDonationsToReassignmentQueueProps = {
  organisationId: string
  payload: {
    reason?: string
    newOrganisationId?: string
    reassignmentKind: ReassignmentKind
  }
}

export type RemoveDonationFromReassignmentQueueProps = {
  donationId: string
}

export type ReassignMarkedDonationProps = {
  donationId: string
  payload: {
    reason?: string
    newOrganisationId?: string
    reassignmentKind?: ReassignmentKind
  }
}

export type ReassignMarkedDonationsProps = {
  organisationId: string
  payload: {
    reason?: string
    newOrganisationId?: string
  }
}

export type ReassignMarkedDonationsResponse = {
  ineligibleDonations: {
    cancelled: number
    disbursed: number
    inActiveDisbursement: number
    ineligibleOrganisation: number
    mismatchedFoundations: number
    missingDonationReassignment: number
    missingOrganisationId: number
    missingReason: number
  }
  reassignedCount: number
}

export type PostReassignAllDonationsProps = {
  organisationId: string
  payload: {
    organisationIdTo: string
    reason: string
    reassignmentKind: ReassignmentKind.Merge
  }
}

export type PostReassignAllDonationsResponse = {
  reassignedCount: number
}

export type PostCancelAllReassignmentsProps = {
  organisationId: string
}

export type PostCancelAllReassignmentsResponse = {
  cancelledDonationReassignmentsCount: number
}

export enum ReassignmentKind {
  Merge = 'MERGE',
  Unpayable = 'UNPAYABLE'
}

export enum ReassignmentStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

export type GetPayrollDeductionReportProps = {
  partnerId: string
  startDate: string
  endDate: string
}

export type PostPayrollDeductionReportsImportProps = {
  payload: string
  partnerId: string
}

export type PayrollDeductionImportResult = {
  donationId: string
  action: 'approved' | 'rejected' | 'approved_partial' | 'approved_excess' | 'already_processed'
  deductedAmount: number
  originalAmount: number
}

export type PayrollDeductionImportError = {
  row: number
  donationId: string
  error: string
  details: string
}

export type PostPayrollDeductionReportsImportResponse = {
  message: string
  processed: number
  summary: {
    approved: number
    rejected: number
    approved_partial: number
    approved_excess: number
    already_processed: number
  }
  results: PayrollDeductionImportResult[]
  errors: PayrollDeductionImportError[]
}

export type PayrollDeductionReportsImportRow = {
  donationId: string
  currencyCode: string
  payrollDeductionAmount: string
}

export type EditDonationProps = {
  donationId: string
  payload: {
    externalPaymentUrl?: string | null
  }
}
